import {GenginePlayerGroup} from './gengine-player-group';
import {throwError, Subject} from 'rxjs';
import {IGenginePlayer} from './i-gengine-player';
import {IMessage} from './i-message';
import {GengineProxyFactory} from './gengine-proxy';
import {IChange, IGengineDataHolder} from './i-gengine-data-holder';
import {IGenginePlayerGroup} from './i-gengine-player-group';
import {catchError, filter, take, timeout} from 'rxjs/operators';
import {merge} from 'rxjs';
import {environment} from '../../../environments/environment';

export interface TWithPlayers {
  players: {[name: string]: any};
}

export class GengineDataHolder<DataT extends TWithPlayers>
  extends GenginePlayerGroup
  implements IGengineDataHolder
{

  constructor(name: string, type: string, player: IGenginePlayer, parent?: IGenginePlayerGroup) {
    super(name, type, player, parent);
    this.onHold( () => { this.mData = this.factory.makeProxy({} as DataT); });
  }

  set data(value: DataT) {
    this.mData = this.factory.makeProxy(value);
    this.addChange({path: '', value: this.factory.toJson(value)});
  }

  get data(): DataT {
    return this.mData;
  }
  changesBuffer: IChange[] = [];
  holdChanges = false;
  factory = new GengineProxyFactory(this);
  mData: DataT;

  public updates = new Subject<IChange>();
  public changes = new Subject<IChange>();

  static atPath<T>(object: any, path: string): T {
    const names = path.split('/').filter(p => p.length);
    let name: string|number;
    let parent = object;
    for (name of names) {
      if (Array.isArray(parent)) {
        name = Number.parseInt(name as string, 10);
      }
      if (!parent.hasOwnProperty(name)) {
        throw new Error('path incomplete');
      }
      parent = parent[name];
    }
    return parent as T;
  }

  static hasPath(object: any, path: string): boolean {
    if (object === undefined) {
      return false;
    }
    const names = path.split('/').filter(p => p.length);
    let name: string|number;
    let parent = object;
    for (name of names) {
      if (Array.isArray(parent)) {
        name = Number.parseInt(name as string, 10);
      }
      if (!parent.hasOwnProperty(name)) {
        return false;
      }
      parent = parent[name];
    }
    return true;
  }

  onHold(fn: () => any): any {
    this.holdChanges = true;
    const result = fn();
    this.holdChanges = false;
    return result;
  }

  // noinspection JSUnusedGlobalSymbols
  async waitForPath<T>(at: string, next = false, timeoutMs = 5000): Promise<T> {
    if (next || !GengineDataHolder.hasPath(this.mData, at)) {
      // this.log(`[DH] Start waiting for ${at}, data is ${JSON.stringify(this.mData)}`);
      const theError = new Error(`[DH] ${this.type} waiting for ${at} with timeout:${timeoutMs} ` + JSON.stringify(this.mData));
      await merge(this.updates, this.changes).pipe(
        filter(({path, value}) => {
          if (!at.startsWith(path)) {
            // console.log(`[DH] ignoring '${path}' while waiting for '${at}'`);
            return false;
          }
          const sub = path.substr(at.length);
          const result = GengineDataHolder.hasPath(value, sub);
          if (!result) {
            // console.log(`[DH] ignoring '${path}/'${JSON.stringify(value)} waiting for '${at}' with sub=${sub}`);
          }
          return result;
        }),
        timeout(timeoutMs),
        catchError(e => {
          theError.message += ' ' + e;
          return throwError(theError);
        }),
        take(1),
      ).toPromise();
    }
    return GengineDataHolder.atPath<T>(this.mData, at);
  }

  addChange(change: IChange): void {
    if (this.holdChanges) { return; }
    if (change.path[0] === '/') {
      change.path = change.path.substr(1);
    }
    this.logChange(change);
    this.changesBuffer.push(change);
    this.changes.next(change);
  }

  private logChange(change: IChange): void {
    if
    (environment.logUpdates &&
      environment.logUpdates[this.type]?.on &&
      (
        !environment.logUpdates[this.type].paths ||
        change.path.match(environment.logUpdates[this.type].paths)
      )
    ) {
      this.log(`[DH] ${this.type}` + JSON.stringify(change));
    }
  }

  private applyUpdates(data: { path: string, value: any }[]): void {
    for (const piece of data) {
      this.onHold(() => this.applyUpdate(piece) );
      this.updates.next(piece);
    }
  }

  leave(): void {
    this.updates.complete();
    super.leave();
  }

  onMessage(message: IMessage): void {
    super.onMessage(message);
    try {
      switch (message.type) {
        case 'game start':
          this.data = message.data;
          // this.data = message.data;
          this.events.next('game_start');
          break;
        case `update ${this.type}`:
          if (!message.data.room || message.data.room === this.name) {
            this.applyUpdates(message.data);
          }
          break;
      }
    } catch (e) {
      console.error(e);
    }
  }

  // noinspection JSUnusedGlobalSymbols
  async sendUpdates(): Promise<void> {
    const data = this.changesBuffer;
    if (data.length) {
      this.changesBuffer = [];
      await this.mPlayer.send({type: `update ${this.type}`, data});
    }
  }

  private applyUpdate(data: { path: string, value: any }): void {
    const names = data.path.split('/').filter(p => p.length);
    if (!this.mData) {
      this.data = {} as DataT;
    }
    if (!names.length) {
      this.data = data.value;
      return;
    }
    let key: number|string = names.splice(names.length - 1, 1)[0];
    let parent = this.mData;
    // ---
    let name: string|number;
    for (name of names) {
      if (Array.isArray(parent)) {
        name = Number.parseInt(name as string, 10);
      }
      if (!parent[name]) {
        parent[name] = {};
      }
      parent = parent[name];
    }
    // ----
    if (Array.isArray(parent)) {
      switch (key) {
        case 'push': parent.push.call(parent, data.value); break;
        case 'splice': parent.splice.call(parent, data.value); break;
        default:
          key = Number.parseInt(key as string, 10);
          parent[key] = data.value;
      }
    } else {
      parent[key] = data.value;
    }
    if (data.path.startsWith('players')) {
      this.updatePlayers();
    }
    this.logChange(data);
  }

  setPlayers(players: string[]): void {
    super.setPlayers(players);
    this.updatePlayers();
  }

  private updatePlayers(): void {
    if (!this.mData || !this.data.players) { return; }
    for (const player of this.players) {
      player[`${this.type}Data`] = this.data.players[player.name];
    }
  }
}
